/**
 * PURPOSE: "Mover" una fila (deportista o equipo de posta) a otra serie de la MISMA prueba.
 * RESPONSIBILITIES: Elegir la serie de destino, el carril (con quién ocupa cada uno) y el orden. Pista: orden = carril por
 *   defecto; si el carril está ocupado (salvo 800/1500, que admiten dos) ofrece INTERCAMBIAR posiciones con el ocupante,
 *   REEMPLAZARLO (el ocupante sale de la serie y vuelve a inscritos sin serie) o elegir otro; concurso: sin carril, al final
 *   por defecto. Si la serie de origen o la de destino está terminada (oficial)
 *   lo dice con texto y no deja mover; si la fila ya tiene marca o estado avisa que viaja con ella y pide confirmarlo.
 *   Los relevistas de un equipo no se tocan. El servidor repite todas las validaciones.
 * COLLABORATORS: PruebaDetailPanel.jsx, RoundEditor.jsx, grids/*, placement.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/MoveEntryModal.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import Button from "@mui/joy/Button";
import { FiAlertTriangle, FiLock } from "react-icons/fi";

import {
  COMBINED_RESULT_REASON,
  LANE_NUMBERS,
  entryHasResult,
  isLaneFull,
  isRoundLocked,
  laneCapacity,
  moveTargetRounds,
  nextOrderOf,
  occupantLabel,
  occupantsOfLane,
  roundName,
  usesLanes,
} from "../placement.js";
import { Spinner, selectClass } from "./ui.jsx";

const MoveEntryModal = ({ open, prueba, rounds, sourceRound, entry, onSubmit, onClose }) => {
  const targets = useMemo(() => moveTargetRounds(rounds, sourceRound), [rounds, sourceRound]);
  const [targetId, setTargetId] = useState(null);
  const [lane, setLane] = useState("");
  const [order, setOrder] = useState("");
  const [swapRowId, setSwapRowId] = useState(null);
  // Carril ocupado: null, "swap" (intercambiar) o "replace" (el ocupante sale de la serie).
  const [mode, setMode] = useState(null);
  const swap = mode === "swap";
  const replace = mode === "replace";
  const [confirmResult, setConfirmResult] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const firstFree = targets.find((round) => !isRoundLocked(round)) || targets[0];
    setTargetId(firstFree ? firstFree.id : null);
    setLane("");
    setOrder("");
    setMode(null);
    setSwapRowId(null);
    setConfirmResult(false);
    setSubmitting(false);
    setError("");
    // Solo al abrir (o al cambiar de fila): no se pisa lo que el operador eligió mientras está abierto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry?.id]);

  const target = targets.find((round) => round.id === targetId) || null;
  const laneMode = usesLanes(prueba);
  const name = occupantLabel(entry);
  const lanePicked = lane ? Number(lane) : null;
  const occupants = laneMode && lanePicked ? occupantsOfLane(target, lanePicked) : [];
  const taken = laneMode && lanePicked ? isLaneFull(prueba, target, lanePicked) : false;
  const swapWith = taken ? (occupants.length === 1 ? occupants[0] : occupants.find((item) => item.id === swapRowId) || null) : null;
  const involved = [entry, ...(taken && (swap || replace) && swapWith ? [swapWith] : [])];
  const withResult = involved.filter(entryHasResult);
  const replacedWithResult = taken && replace && swapWith && entryHasResult(swapWith);

  const lockedMessage = isRoundLocked(sourceRound)
    ? `${roundName(sourceRound)} está terminada (oficial): reábrela para mover filas.`
    : target && isRoundLocked(target)
      ? `${roundName(target)} está terminada (oficial): no se pueden agregar filas.`
      : "";

  const missing = lockedMessage
    ? lockedMessage
    : !target
      ? "No hay otra serie a la que mover."
      : laneMode && !lanePicked
        ? "Elige el carril."
        : taken && !swap && !replace
          ? "El carril está ocupado: elige otro, intercámbialos o reemplaza al ocupante."
          : taken && (swap || replace) && !swapWith
            ? "Elige a quién."
            : replacedWithResult && prueba?.parentId
              ? COMBINED_RESULT_REASON
              : withResult.length && !confirmResult
                ? "Confirma lo que pasa con el resultado."
                : "";

  const submit = async () => {
    if (missing || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const outcome = await onSubmit({
        targetRoundId: target.id,
        lane: laneMode ? lanePicked : null,
        order: (taken && (swap || replace)) || order === "" ? null : Number(order),
        swap: taken && swap,
        swapRowId: taken && swap && swapWith ? swapWith.id : null,
        replace: taken && replace,
        replaceRowId: taken && replace && swapWith ? swapWith.id : null,
        confirmResult: withResult.length > 0,
        entry,
        target,
      });
      if (outcome?.ok || outcome?.close) onClose();
      else setError(outcome?.message || "No se pudo mover.");
    } finally {
      setSubmitting(false);
    }
  };

  const defaultOrder = laneMode ? lanePicked || "" : target ? nextOrderOf(target) : "";

  return (
    <Modal open={Boolean(open)} onClose={submitting ? undefined : onClose}>
      <ModalDialog sx={{ width: "min(96vw, 640px)", maxHeight: "92vh", borderRadius: "16px", overflow: "auto" }}>
        <ModalClose disabled={submitting} />
        <DialogTitle>Mover a {name}</DialogTitle>
        <DialogContent>
          <p className="text-xs font-semibold text-slate-500">
            Ahora: {roundName(sourceRound)}
            {entry?.lane ? ` · carril ${entry.lane}` : ""}
            {entry?.order ? ` · orden ${entry.order}` : ""}. Solo se mueve esta fila; nadie más cambia de lugar.
            {entry?.participant?.type === "team" ? " Los relevistas del equipo no se tocan." : ""}
          </p>

          {lockedMessage ? (
            <p role="alert" className="mt-3 flex items-start gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700">
              <FiLock className="mt-0.5 shrink-0" aria-hidden="true" /> {lockedMessage}
            </p>
          ) : null}

          {!targets.length ? (
            <p className="mt-3 text-sm text-slate-600">Esta prueba no tiene otra serie a la que mover.</p>
          ) : (
            <div className="mt-3 space-y-3">
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Serie de destino</span>
                <select
                  value={targetId ?? ""}
                  onChange={(event) => {
                    setTargetId(Number(event.target.value));
                    setLane("");
                    setMode(null);
                    setSwapRowId(null);
                  }}
                  className={selectClass}
                  aria-label="Serie de destino"
                >
                  {targets.map((round) => (
                    <option key={round.id} value={round.id} disabled={isRoundLocked(round)}>
                      {roundName(round)}
                      {isRoundLocked(round) ? " (terminada)" : ""}
                    </option>
                  ))}
                </select>
              </label>

              {laneMode ? (
                <fieldset>
                  <legend className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                    Carril en {roundName(target)}
                    {laneCapacity(prueba) > 1 ? " (800/1500: caben dos por carril)" : ""}
                  </legend>
                  <div role="radiogroup" aria-label="Carril de destino" className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-3">
                    {LANE_NUMBERS.map((number) => {
                      const list = occupantsOfLane(target, number);
                      const full = isLaneFull(prueba, target, number);
                      const active = lanePicked === number;
                      return (
                        <button
                          key={number}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => {
                            setLane(String(number));
                            setMode(null);
                            setSwapRowId(null);
                          }}
                          title={list.length ? list.map(occupantLabel).join(" · ") : "Libre"}
                          className={`rounded-lg border px-2 py-1 text-left text-xs transition ${
                            active
                              ? "border-indigo-500 bg-indigo-50 font-bold text-indigo-800"
                              : full
                                ? "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"
                                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <span className="font-black">Carril {number}</span>
                          <span className="block truncate text-[11px] text-slate-500">
                            {list.length ? `${full ? "Ocupado" : "1 de 2"}: ${list.map(occupantLabel).join(" · ")}` : "Libre"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                  Prueba de campo: sin carril. Va al final del orden de salida (N.º {target ? nextOrderOf(target) : "—"}) salvo que indiques otro orden.
                </p>
              )}

              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                  Orden de salida {laneMode ? "(por defecto = carril)" : "(por defecto al final)"}
                </span>
                <input
                  type="number"
                  min={1}
                  max={999}
                  inputMode="numeric"
                  value={order}
                  onChange={(event) => setOrder(event.target.value)}
                  placeholder={String(defaultOrder)}
                  aria-label="Orden de salida"
                  disabled={taken && (swap || replace)}
                  className={`${selectClass} w-28`}
                />
              </label>

              {taken ? (
                <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="flex items-start gap-2 text-xs font-bold text-amber-900">
                    <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
                    El carril {lanePicked} de {roundName(target)} está ocupado por {occupants.map(occupantLabel).join(" y ")}.
                  </p>
                  {(swap || replace) && occupants.length > 1 ? (
                    <div role="radiogroup" aria-label={swap ? "Intercambiar con" : "Reemplazar a"} className="mt-2 space-y-1">
                      {occupants.map((item) => (
                        <label key={item.id} className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                          <input type="radio" name="swap-with" checked={swapRowId === item.id} onChange={() => setSwapRowId(item.id)} />
                          {swap ? "Intercambiar con" : "Reemplazar a"} {occupantLabel(item)}
                        </label>
                      ))}
                    </div>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant={swap ? "solid" : "outlined"} color="warning" onClick={() => setMode((value) => (value === "swap" ? null : "swap"))}>
                      {swap ? "Intercambio activado" : "Intercambiar"}
                    </Button>
                    <Button size="sm" variant={replace ? "solid" : "outlined"} color="warning" onClick={() => setMode((value) => (value === "replace" ? null : "replace"))}>
                      {replace ? "Reemplazo activado" : "Reemplazar"}
                    </Button>
                    <span className="self-center text-[11px] text-amber-800">
                      {swap
                        ? `${name} toma ese carril y el ocupante pasa a ${roundName(sourceRound)}${entry?.lane ? `, carril ${entry.lane}` : ""}.`
                        : replace
                          ? `${name} toma ese carril y el ocupante sale de la serie (vuelve a inscritos sin serie).`
                          : "O elige otro carril."}
                    </span>
                  </div>
                </div>
              ) : null}

              {withResult.length ? (
                <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="flex items-start gap-2 text-xs font-bold text-amber-900">
                    <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
                    {withResult.map(occupantLabel).join(" y ")} {withResult.length > 1 ? "ya tienen" : "ya tiene"} marca o estado
                    {replacedWithResult ? `: el de ${occupantLabel(swapWith)} se pierde al reemplazarlo; el resto se mueve con su fila.` : ": se mueve con la fila."}
                  </p>
                  <label className="mt-2 flex items-center gap-2 text-xs font-bold text-amber-900">
                    <input type="checkbox" checked={confirmResult} onChange={(event) => setConfirmResult(event.target.checked)} />
                    Entiendo, mover con su resultado.
                  </label>
                </div>
              ) : null}
            </div>
          )}

          {error ? (
            <p role="alert" className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700">
              {error}
            </p>
          ) : null}
          {missing && !lockedMessage && targets.length ? <p className="mt-2 text-xs font-semibold text-slate-500">{missing}</p> : null}
        </DialogContent>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="plain" color="neutral" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" color="primary" onClick={submit} disabled={Boolean(missing) || submitting} startDecorator={submitting ? <Spinner /> : null}>
            {submitting ? "Moviendo..." : taken && swap ? "Intercambiar y mover" : taken && replace ? "Reemplazar y mover" : "Mover"}
          </Button>
        </div>
      </ModalDialog>
    </Modal>
  );
};

export default MoveEntryModal;
